import type { FC, FormEvent } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

type Status = 'idle' | 'loading' | 'success' | 'error'

interface NewsletterFormProps {
    source: string
    tone?: 'light' | 'dark'
    className?: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const NewsletterForm: FC<NewsletterFormProps> = ({ source, tone = 'light', className }) => {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const id = useId()
    const dark = tone === 'dark'

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const value = email.trim()

        if (!EMAIL_PATTERN.test(value)) {
            setStatus('error')
            setMessage('That address looks a bit off. Try again?')

            return
        }

        setStatus('loading')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: value, source })
            })

            const data = await response.json().catch(() => ({})) as { message?: string }

            if (!response.ok) throw new Error(data.message || 'Something went wrong. Please try again.')

            setStatus('success')
            setMessage("You're on the list. The first Signal lands on Tuesday.")
            setEmail('')
        }
        catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
        }
    }

    return (
        <form
            onSubmit={onSubmit}
            noValidate
            className={className}
        >
            <label
                htmlFor={id}
                className='sr-only'
            >
                Email address
            </label>

            <div className={cn(
                'flex items-center rounded-full border p-1.5 transition-colors focus-within:border-ember',
                dark ? 'border-cream/15 bg-cream/5' : 'border-ink/10 bg-white/70'
            )}>
                <input
                    id={id}
                    type='email'
                    name='email'
                    autoComplete='email'
                    inputMode='email'
                    placeholder='you@yourbrand.com'
                    value={email}
                    onChange={event => {
                        setEmail(event.target.value)

                        if (status === 'error') setStatus('idle')
                    }}
                    aria-invalid={status === 'error'}
                    aria-describedby={`${id}-message`}
                    className={cn(
                        'min-w-0 flex-1 bg-transparent px-4 text-[15px] outline-none',
                        dark ? 'text-cream placeholder:text-cream/45' : 'text-ink placeholder:text-ink/40'
                    )}
                />

                <button
                    type='submit'
                    disabled={status === 'loading'}
                    className='group/sub inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-ember px-5 text-sm font-medium text-ink transition-colors hover:bg-[#ff6a14] disabled:opacity-70'
                >
                    {status === 'loading' ? 'Sending' : status === 'success' ? 'Signed up' : 'Get the Signal'}
                    {status === 'success'
                        ? <Check className='size-4' />
                        : <ArrowRight className='size-4 transition-transform duration-300 group-hover/sub:translate-x-0.5' />}
                </button>
            </div>

            <p
                id={`${id}-message`}
                role='status'
                aria-live='polite'
                className={cn(
                    'mt-3 min-h-5 text-sm',
                    status === 'error' ? 'text-[#c2330a]' : dark ? 'text-cream/70' : 'text-stone',
                    status === 'error' && dark && 'text-[#ff9a66]'
                )}
            >
                {message}
            </p>
        </form>
    )
}
